/* eslint-disable no-restricted-globals */

import * as Sentry from '@sentry/vue'
import PricingApi from '@/api/pricing'
import PromoApi from '@/api/promo'
import utils from '@/util'
import router from '@/router'
import InvoiceApi from '@/api/invoice'
import mixpanelUtils from '@/utils/mixpanel.utils'
import { MONTHLY_YEARLY_UPGRADE_PLAN_MAP } from '@/constant/monthlyToYearlyUpgrade'
import { ROUTE_NAMES } from '@/constant/routes'
import { DIRECT_BULK_PURCHASE_STATUS } from '@/constant/directBulkPurchase'

const DEFAULT_TOAST_ERROR_MESSAGE = 'Something went wrong, please check your connection or contact us for support'

const getTrackingData = () => {
  try {
    return mixpanelUtils.getProperties()
  } catch (e) {
    Sentry.captureException(e)
    return undefined
  }
}

const createCheckoutSessionAction = async ({ commit, dispatch }, payload) => {
  try {
    commit('isCreatingCheckoutSession', true)

    // Use the v4 bulk checkout endpoint: it accepts a Stripe lookupKey
    // (variant-aware) and resolves it to a plan whose canonical `.type` is
    // used by the multi-license coupon lookup. The legacy V2 endpoint keyed
    // the coupon off the raw lookupKey, so Unleash price-variant lookupKeys
    // (e.g. `pro-yearly-vietnam-50pct-20260507`) silently lost the bulk
    // discount.
    const res = await PricingApi.createBulkCheckoutSessionV4({
      lookupKey: payload.plan,
      quantity: payload.quantity,
      origin: window.location.href,
      trackingData: getTrackingData(),
      ...(payload.priceVariantStandard ? { priceVariantStandard: payload.priceVariantStandard } : {}),
      ...(payload.priceVariantEdu ? { priceVariantEdu: payload.priceVariantEdu } : {}),
      ...(payload.pricingSurface ? { pricingSurface: payload.pricingSurface } : {}),
    })
    if (res.sessionUrl) {
      window.location.href = res.sessionUrl
    }
  } catch (e) {
    if (e.body && e.body.message) {
      commit('createCheckoutSessionErrorMutation', e.body.message)
    }
    dispatch('hookBulkPricingAction', {
      planType: payload.plan,
      numOfLicenses: payload.quantity,
      email: payload.email,
      requirement: 'This was initiated because of failure during checkout session creation.',
    })
    throw e
  } finally {
    commit('isCreatingCheckoutSession', false)
  }
}
const createCheckoutSessionForPersonPlan = async ({ commit }, payload) => {
  try {
    commit('isCreatingCustomerPortal', true)
    const {
      plan: planType, promotionCode, query, pricingSurface,
      priceVariantStandard, priceVariantEdu, currency,
    } = payload
    commit('loadingPlan', planType)

    const data = await PricingApi.createCheckoutSessionForPersonPlan({
      planType,
      promotionCode,
      query,
      trackingData: getTrackingData(),
      ...(currency ? { currency } : {}),
      // Forwarded from aha-pricing → register → stripe-transition. The BE
      // controller tags the Mixpanel user profile with
      // `pricing_standard_variant` / `pricing_edu_variant` and stamps both
      // fields into Stripe metadata so the `invoice.payment_succeeded` →
      // Mixpanel `Make payment` event carries attribution. Logged-in buyers
      // on /pricing skip this path entirely (they call v4 directly); this is
      // the anonymous-first parity fix. Two scoped fields prevent collision
      // between the standard pricing A/B test and the edu pricing A/B test.
      ...(pricingSurface ? { pricingSurface } : {}),
      ...(priceVariantStandard ? { priceVariantStandard } : {}),
      ...(priceVariantEdu ? { priceVariantEdu } : {}),
    })
    if (data && data.sessionUrl) {
      window.location.href = data.sessionUrl
    }
  } catch (e) {
    Sentry.captureException(e)
    const errorMessage = e.body?.message || DEFAULT_TOAST_ERROR_MESSAGE
    utils.showToastError(
      errorMessage,
    )
    return e
  } finally {
    commit('isCreatingCustomerPortal', false)
    commit('loadingPlan', null)
  }
}
const createCustomerPortalUpgradeFlowAction = async ({ commit }, payload) => {
  try {
    commit('isCreatingCustomerPortal', true)
    commit('loadingPlan', payload.plan)

    const data = await PricingApi.createCustomerPortalUpgradeFlowV2(payload)
    commit('isCreatingCustomerPortal', false)
    if (data && data.url) {
      window.location.href = data.url
      commit('loadingPlan', null)
    }
  } catch (e) {
    const errorMessage = e.body?.message || DEFAULT_TOAST_ERROR_MESSAGE
    utils.showToastError(
      errorMessage,
    )
    commit('isCreatingCustomerPortal', false)
    commit('loadingPlan', null)
    Sentry.captureException(e)
    return e
  }
}
const createCustomerPortalUpgradeFlowV3Action = async ({ commit }, payload) => {
  try {
    commit('isCreatingCustomerPortal', true)
    commit('loadingPlan', payload.plan)

    const data = await PricingApi.createCustomerPortalUpgradeFlowV3(payload)
    commit('isCreatingCustomerPortal', false)
    if (data && data.url) {
      commit('loadingPlan', null)
      window.location.href = data.url
    }
    if (data?.message) {
      utils.showToastSuccess(data.message)
    }
  } catch (e) {
    const errorMessage = e?.body?.message || DEFAULT_TOAST_ERROR_MESSAGE
    utils.showToastError(
      errorMessage,
    )
    Sentry.captureException(e)
  } finally {
    commit('isCreatingCustomerPortal', false)
    commit('loadingPlan', null)
  }
}
const createCustomerPortalAction = async ({ commit }, payload) => {
  try {
    commit('isCreatingCustomerPortal', true)
    const data = await PricingApi.createCustomerPortal({
      pathname: window.location.pathname,
      isUpdatingPlan: payload ? payload.isUpdatingPlan : false,
    })
    commit('isCreatingCustomerPortal', false)
    if (data && data.url) {
      window.location.href = data.url
    }
  } catch (e) {
    const errorMessage = e.body?.message || DEFAULT_TOAST_ERROR_MESSAGE
    utils.showToastError(
      errorMessage,
    )
    commit('isCreatingCustomerPortal', false)
    Sentry.captureException(e)
  }
}
const hookBulkPricingAction = async ({ commit }, payload) => {
  try {
    return PricingApi.hookBulkPricing(payload)
  } catch (e) {
    Sentry.captureException(e)
    commit('hookBulkPricingErrorMutation', e)
  }
}
const getPromotionCode = async ({ commit }, payload) => {
  const { promotionCode } = await PromoApi.checkPromotion({
    promo: payload,
  })
  commit('setPromotionCode', promotionCode)
}

const setIsValidPromoCodeAction = async ({ commit }, payload) => {
  commit('setIsValidPromoCode', payload)
}
const updateRemoveCardPurposeAction = ({ commit }, payload) => {
  commit('setRemoveCardPurpose', payload)
}

const requestAdditionalLicensePopupAction = async ({ commit }, payload) => {
  commit('isShowingRequestAdditionalLicensePopup', payload)
}

const goToBulkLicensePageAction = () => {
  router.push({
    name: 'UserPlan',
    hash: '#bulk-pricing',
  })
}
const trackNewPurchaseAction = (
  { state }, payload,
) => {
  const { currentUser, plan } = state

  if (payload.query) {
    setTimeout(async () => {
      const res = await InvoiceApi.getInvoices()

      if (res.invoices && res.invoices.length === 0) {
        return
      }
      const invoice = res.invoices[0]

      const { source, type } = payload.query

      let coupon = ''
      if (invoice.coupon) {
        coupon = invoice.coupon.name
      }
      let discount = 0
      if (invoice.lines && invoice.lines.length > 0) {
        discount = invoice.lines[invoice.lines.length - 1].discountAmountRaw
      }
      const payment_method = invoice.paymentMethod.brand === 'visa' ? 'Stripe > Cards' : 'Stripe > iDEAL with SEPA'
      const params = {
        currency: invoice.currency,
        ahaslides_user_id: currentUser.id,
        transaction_id: invoice.invoiceId,
        value: invoice.amountPaidRaw,
        coupon,
        tax: 0,
        item_name: plan.name,
        discount,
        payment_method,
        item_category: plan.group,
        item_category2: plan.billingCycle,
        item_category3: type,
        item_variant: source === 'landing_site' ? 'Landing site' : 'Presenter app',
      }
      utils.trackGA4AndMixpanel('purchase', {
        ...params,
      })
      router.replace({ query: null })
    }, 1000)
  }
}

const hookCustomRequestAction = async ({ commit }, payload) => {
  try {
    return PricingApi.hookCustomRequest(payload)
  } catch (e) {
    Sentry.captureException(e)
    commit('hookCustomRequestError', e)
  }
}

const submitEnterpriseInquiriesFormAction = async (_, payload) => {
  try {
    return PricingApi.submitEnterpriseInquiriesForm({ ...payload, source: getTrackingData() })
  } catch (e) {
    Sentry.captureException(e)
  }
}

export const getDiscountPercentageFromVariant = (unleashVariant) => {
  const defaultDiscountPercentage = 40
  if (
    !unleashVariant ||
    unleashVariant.name !== 'discountPercentage' ||
    !unleashVariant?.payload?.value ||
    unleashVariant?.payload?.value === '0' ||
    isNaN(unleashVariant.payload.value)
  ) {
    return defaultDiscountPercentage
  }
  if (unleashVariant.payload.value < 0 || unleashVariant.payload.value > 100) return defaultDiscountPercentage
  return Number(unleashVariant.payload.value)
}

const getMonthlyToYearlyUpgradePreviewAction = async ({ commit, state, getters }) => {
  try {
    const currentPlan = state.currentUser.plan
    const upgradePlan = MONTHLY_YEARLY_UPGRADE_PLAN_MAP[currentPlan]
    if (!upgradePlan) {
      return
    }

    const variant = getters['featureToggles/getVariant']('AHA-39538-monthly-to-yearly')
    const res = await PricingApi.getMonthlyToYearlyUpgradePreview({
      plan: upgradePlan,
      // dynamically get the discountPercentage from Unleash, so the Growth team can configure it on the fly without touching the code
      // in case Unleash has problem fetching the value, we fallback to 40% as the safety net.
      discountPercentage: getDiscountPercentageFromVariant(variant),
    })
    commit('setMonthlyToYearlyUpgradePreview', res)
  } catch (e) {
    Sentry.captureException(e)
    commit('setMonthlyToYearlyUpgradePreview', null)
  }
}

const upgradeFromMonthlyToYearlyAction = async ({
  getters, commit, dispatch, state,
}) => {
  try {
    if (state.isUpgradingFromMonthlyToYearly) {
      return
    }
    commit('setUpgradingFromMonthlyToYearly', true)
    const currentPlan = state.currentUser.plan
    const planToUpgrade = MONTHLY_YEARLY_UPGRADE_PLAN_MAP[currentPlan]
    if (!currentPlan || !planToUpgrade) {
      utils.showToastError(
        'The plan is not eligible for upgrade to yearly plan',
      )
      return
    }
    const payload = {
      plan: planToUpgrade,
    }
    if (getters.monthlyToYearlyUpgradePromotionCode) {
      payload.promotionCode = getters.monthlyToYearlyUpgradePromotionCode
    }
    await dispatch('createCustomerPortalUpgradeFlowAction', payload)
  } catch (e) {
    Sentry.captureException(e)
    utils.showToastError(
      DEFAULT_TOAST_ERROR_MESSAGE,
    )
  } finally {
    commit('setUpgradingFromMonthlyToYearly', false)
  }
}

const setShowMonthlyToYearlyPromoBannerAction = ({ commit }, payload) => {
  commit('setIsShowMonthlyToYearlyPromoBanner', payload)
}

const upgradePlanAction = async ({ state, dispatch }, payload) => {
  if (!state.isAuthenticated) {
    router.replace({
      name: ROUTE_NAMES.LOGIN,
      query: {
        redirect: window.location.href,
      },
    })
    return
  }
  if (state.currentUser.plan === 'free') {
    await dispatch('createCheckoutSessionForPersonPlan', payload)
  } else {
    await dispatch('createCustomerPortalUpgradeFlowV3Action', payload)
  }
}

// AHA-38492: Simplified to handle direct bulk purchase via single API call
const bulkPurchaseRedirectAction = async ({ commit, getters }, payload) => {
  // Not logged in → redirect to signup
  if (payload.user.id === null) {
    router.replace({
      name: ROUTE_NAMES.LOGIN,
      query: {
        redirect: window.location.href,
      },
    })
    return
  }

  if (!getters['featureToggles/canUseDirectBulkPurchaseLinks']) {
    commit('setDirectBulkPurchaseStatus', DIRECT_BULK_PURCHASE_STATUS.INVALID_PARAMS)
    return
  }

  // Validate basic params
  if (!payload || !payload.plan || !payload.quantity) {
    commit('setDirectBulkPurchaseStatus', DIRECT_BULK_PURCHASE_STATUS.INVALID_PARAMS)
    return
  }

  try {
    // Call unified API - backend handles all logic
    const response = await PricingApi.handleDirectBulkPurchase({
      plan: payload.plan,
      quantity: payload.quantity,
    })

    if (response.canCheckout && response.sessionUrl) {
      // User can checkout - redirect to Stripe
      window.location.href = response.sessionUrl
    } else {
      // User needs sales assistance - show confirmation message
      commit('setDirectBulkPurchaseStatus', DIRECT_BULK_PURCHASE_STATUS.ORDER_RECEIVED)
    }
  } catch (error) {
    commit('setDirectBulkPurchaseStatus', DIRECT_BULK_PURCHASE_STATUS.PURCHASE_ERROR)
  }
}

export default {
  createCheckoutSessionAction,
  createCheckoutSessionForPersonPlan,
  hookBulkPricingAction,
  updateRemoveCardPurposeAction,
  createCustomerPortalAction,
  createCustomerPortalUpgradeFlowAction,
  requestAdditionalLicensePopupAction,
  goToBulkLicensePageAction,
  trackNewPurchaseAction,
  hookCustomRequestAction,
  getPromotionCode,
  setIsValidPromoCodeAction,
  submitEnterpriseInquiriesFormAction,
  upgradeFromMonthlyToYearlyAction,
  getMonthlyToYearlyUpgradePreviewAction,
  setShowMonthlyToYearlyPromoBannerAction,
  upgradePlanAction,
  createCustomerPortalUpgradeFlowV3Action,
  bulkPurchaseRedirectAction,
}
